/**
 * Why a source could not be read, and what to tell the user about it.
 *
 * The extract status endpoint reports a `reason` on a failed source when it
 * knows why the site could not be read (CON-8354). Without it, a site that
 * blocks bots read as "We hit a problem reading your sources" next to a Try
 * again that could never work; with it, the user is told what happened and
 * what to add instead.
 */

import type { DsGlobalStylesProcessSourceStatus } from '@/services/db/ds-global-styles/entity';

/** Why the status endpoint says a failed source couldn't be read. */
export type SourceFailureReason = NonNullable<DsGlobalStylesProcessSourceStatus['reason']>;

/**
 * Every reason, for the places that check one at runtime (the persisted draft,
 * a poll from a newer backend), where a reason missing from this list would be
 * dropped without a sound. `satisfies` keeps the list within the type and
 * `_everyReasonListed` keeps the type within the list, so a reason the spec adds
 * fails to compile until it is listed here and has copy below.
 */
export const SOURCE_FAILURE_REASONS = [
  'blocked',
  'not_found',
  'unreachable',
  'timeout',
  'empty',
  'site_error',
] as const satisfies readonly SourceFailureReason[];

type UnlistedReason = Exclude<SourceFailureReason, (typeof SOURCE_FAILURE_REASONS)[number]>;
// Fails to compile, naming the reason, when one is missing from the list.
const _everyReasonListed: [UnlistedReason] extends [never] ? true : UnlistedReason = true;

/** Failure reasons keyed by source id; a source with no known reason is absent. */
export type SourceFailures = Record<string, SourceFailureReason>;

/** The known reason `value` names, or `undefined` for anything else. */
function parseSourceFailureReason(value: unknown): SourceFailureReason | undefined {
  return SOURCE_FAILURE_REASONS.find((reason) => reason === value);
}

/** Picked from the generated status type, so a renamed field fails to compile rather than hiding the reason. */
type PolledSource = Pick<DsGlobalStylesProcessSourceStatus, 'id' | 'status' | 'reason'>;

/**
 * The failure reasons in a status poll's sources, keyed by source id. A reason
 * outside the known set (a newer backend) is dropped, so the caller falls back
 * to its generic copy rather than rendering nothing.
 */
export function toSourceFailures(sources: ReadonlyArray<PolledSource> | null | undefined) {
  const failures: SourceFailures = {};
  for (const source of sources ?? []) {
    const reason = parseSourceFailureReason(source.reason);
    if (source.status === 'failed' && reason != null) failures[source.id] = reason;
  }
  return failures;
}

/**
 * What a sentence about the failed site opens on: "This site" under its row, or
 * "It" after a sentence that named it. Never the site's own name: a sentence
 * has to open on a capital, and a domain is lowercase ("Bigbox.cl" names a
 * different thing), so a name goes mid-sentence instead.
 */
type SiteSubject = 'This site' | 'It';

/** What happened to the source, as a sentence about `site`. */
const WHAT_HAPPENED = {
  blocked: (site: SiteSubject) => `${site} blocks automated visitors.`,
  not_found: (site: SiteSubject) => `${site} returned "page not found".`,
  unreachable: (site: SiteSubject) => `${site} couldn't be reached.`,
  timeout: (site: SiteSubject) => `${site} took too long to load.`,
  empty: (site: SiteSubject) => `${site} had no content we could read.`,
  site_error: (site: SiteSubject) => `${site} returned an error, so it may be down.`,
} satisfies Record<SourceFailureReason, (site: SiteSubject) => string>;

/** What the user can do about it. */
const NEXT_STEP = {
  blocked: 'Add a screenshot of the site or an exported email instead.',
  not_found: 'Check the address, or add a screenshot of the site instead.',
  unreachable: 'Check the address, or add a screenshot of the site instead.',
  timeout: 'Try again later, or add a screenshot of the site instead.',
  empty: 'Add a screenshot of the site or an exported email instead.',
  site_error: 'Try again later, or add a screenshot of the site instead.',
} satisfies Record<SourceFailureReason, string>;

/** The line under a failed source's row: what happened, about "this site". */
export function sourceFailureRowCopy(reason: SourceFailureReason): string {
  return WHAT_HAPPENED[reason]('This site');
}

/** "a", "a and b", "a, b, and c": the Pluma content guidelines' Oxford comma, which two names don't take. */
function listOf(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names.at(-1)}`;
}

/**
 * The reasons that explain why a whole run failed, or `null` when they don't.
 *
 * A source's reason lands as soon as that source fails, while the rest of the
 * run carries on, so a reason on the poll is not by itself the run's cause: the
 * run may have stalled, lost its status, or failed in the combined analysis
 * after another source was read. The backend fails a run on its sources only
 * when none of them could be read, so that is the one outcome the reasons
 * explain — a `failed` status with every polled source failed.
 */
export function runFailures(
  response: { status: string; sources?: ReadonlyArray<PolledSource> | null } | null | undefined,
): SourceFailures | null {
  const sources = response?.sources ?? [];
  if (response?.status !== 'failed' || sources.length === 0) return null;
  if (!sources.every((source) => source.status === 'failed')) return null;
  const failures = toSourceFailures(sources);
  return Object.keys(failures).length > 0 ? failures : null;
}

/**
 * Why a run that read none of its sources failed, and what to do, or `null`
 * when no source carries a reason (the caller keeps its generic copy).
 * `failures` comes from `runFailures`, so every one of `sources` failed.
 *
 * Names every source, explained or not — leaving one out would send the user
 * back to fail on it. With only one, says what happened to it too. With
 * several, takes the next step from the first that has a reason, since every
 * next step offers the same way out: a screenshot.
 */
export function runFailureDescription(
  sources: ReadonlyArray<{ id: string; label: string }>,
  failures: Readonly<SourceFailures>,
): string | null {
  const explained = sources.find((source) => failures[source.id] != null);
  const reason = explained == null ? undefined : failures[explained.id];
  if (explained == null || reason == null) return null;
  const couldNotRead = `We couldn't read ${listOf(sources.map((source) => source.label))}.`;
  if (sources.length === 1) return `${couldNotRead} ${WHAT_HAPPENED[reason]('It')} ${NEXT_STEP[reason]}`;
  return `${couldNotRead} ${NEXT_STEP[reason]}`;
}
